import {css, StyleSheet} from 'aphrodite';
import type * as monaco from 'monaco-editor';
import React, {useEffect, useRef} from 'react';

import {isBrowser} from 'lib/Browser';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import useMonaco from './useMonaco';

const DIFF_EDITOR_DEFAULTS: monaco.editor.IDiffEditorOptions = {
  automaticLayout: true,
  diffAlgorithm: 'advanced',
  diffCodeLens: true,
  diffWordWrap: 'inherit',
  renderSideBySide: true,
  scrollBeyondLastLine: false,
  wordWrap: 'on',
};

export interface MonacoDiffEditorProps {
  width: string | number;
  height: string | number;
  value: string;
  original: string;
  language: string;
  options?: monaco.editor.IDiffEditorOptions;
  editorDidMount?: (editor: monaco.editor.IStandaloneDiffEditor) => void;
  editorWillUnmount?: (editor: monaco.editor.IStandaloneDiffEditor) => void;
  onChange?: (value: string) => void;
  onChangeContentSize?: (e: monaco.editor.IContentSizeChangedEvent) => void;
  className?: string;
  theme: string;
}

export default function MonacoDiffEditor({
  width,
  height,
  value,
  language,
  options,
  editorDidMount,
  editorWillUnmount,
  onChange,
  onChangeContentSize,
  className,
  original,
  theme,
}: MonacoDiffEditorProps): JSX.Element {
  const monaco = useMonaco();
  const containerElement = useRef<HTMLDivElement | null>(null);
  const editor = useRef<monaco.editor.IStandaloneDiffEditor | null>(null);
  const onDidChangeContentSubscription = useRef<monaco.IDisposable | null>(null);
  const onDidContentSizeChangeSubscription = useRef<monaco.IDisposable | null>(null);
  const shouldPreventOnChange = useRef<boolean | null>(null);

  const handleEditorDidMount = (): void => {
    editorDidMount?.(editor.current!);

    const {modified} = editor.current!.getModel()!;
    onDidChangeContentSubscription.current = modified.onDidChangeContent(() => {
      if (!shouldPreventOnChange.current) {
        onChange?.(modified.getValue());
      }
    });

    // @ts-expect-error for some reason this is not in the DiffEditor interface, but appears to work fine
    onDidContentSizeChangeSubscription.current = editor.current!.onDidContentSizeChange(
      (e: monaco.editor.IContentSizeChangedEvent) => {
        onChangeContentSize?.(e);
      }
    );
  };

  const handleEditorWillUnmount = (): void => {
    editorWillUnmount?.(editor.current!);
  };

  useEffect(
    () => {
      if (containerElement.current && monaco) {
        editor.current = monaco.editor.createDiffEditor(containerElement.current, {
          theme,
          ...DIFF_EDITOR_DEFAULTS,
          ...options,
        });

        editor.current.setModel({
          original: monaco.editor.createModel(original, language),
          modified: monaco.editor.createModel(value, language),
        });
        handleEditorDidMount();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monaco]
  );

  useEffect(() => {
    if (editor.current) {
      editor.current.updateOptions({
        ...DIFF_EDITOR_DEFAULTS,
        ...options,
      });
    }
  }, [className, options]);

  useEffect(() => {
    if (editor.current && monaco) {
      const {original: originalEditor, modified} = editor.current.getModel()!;
      monaco.editor.setModelLanguage(originalEditor, language);
      monaco.editor.setModelLanguage(modified, language);
    }
  }, [language, monaco]);

  useEffect(() => {
    if (monaco) {
      monaco.editor.setTheme(theme);
    }
  }, [theme, monaco]);

  useEffect(() => {
    if (editor.current) {
      const {modified} = editor.current.getModel()!;
      if (value === modified.getValue()) {
        return;
      }
      shouldPreventOnChange.current = true;
      editor.current.getModifiedEditor().pushUndoStop();
      // pushEditOperations says it expects a cursorComputer, but doesn't seem to need one
      // @ts-expect-error
      modified.pushEditOperations(
        [],
        [
          {
            range: modified.getFullModelRange(),
            text: value,
          },
        ]
      );
      editor.current.getModifiedEditor().pushUndoStop();
      shouldPreventOnChange.current = false;
    }
  }, [value]);

  useEffect(() => {
    if (editor.current) {
      const {original: originalEditor} = editor.current.getModel()!;
      if (original !== originalEditor.getValue()) {
        originalEditor.setValue(original);
      }
    }
  }, [original]);

  useEffect(
    () => () => {
      if (editor.current) {
        handleEditorWillUnmount();
        editor.current.dispose();
        const {original: originalEditor, modified} = editor.current.getModel()!;
        if (originalEditor) {
          originalEditor.dispose();
        }
        if (modified) {
          modified.dispose();
        }
      }
      if (onDidChangeContentSubscription.current) {
        onDidChangeContentSubscription.current.dispose();
      }
      if (onDidContentSizeChangeSubscription.current) {
        onDidContentSizeChangeSubscription.current.dispose();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (!isBrowser) {
    // monaco editor is not supported outside of browser contexts e.g. node
    // to make this work in the context of unit tests fall back to two textareas
    return (
      <div className={css(styles.verticalSplit)}>
        <textarea value={original} readOnly={!options?.originalEditable} />
        <textarea
          value={value}
          readOnly={options?.readOnly ?? false}
          onChange={(e) => {
            onChange?.(e.target.value);
          }}
        />
      </div>
    );
  }

  if (!monaco) {
    // show loading animation while we load the monaco bundle
    return <CenteredHorizontalThrobber />;
  }

  return <div ref={containerElement} className={className} style={{height, width}} />;
}

const styles = StyleSheet.create({
  verticalSplit: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
  },
});
